/**
 * Permission checking utilities for rewards-admin
 *
 * Provides consistent permission checking logic across the application.
 * Used by ProtectedRoute, HasPermission, Sidebar, and other components.
 */

import { UserProfile } from '@/app/contexts/AuthContext';

/**
 * Check if a user has a specific permission.
 *
 * Permission hierarchy:
 * - Administrator role has full access to everything
 * - Wildcard permission (*) grants access to everything
 * - Category wildcard (e.g., 'security:*') grants access to all permissions in that category
 * - Exact permission match grants specific access
 *
 * @param userProfile - The user's profile from AuthContext
 * @param permission - The required permission (e.g., 'security:manage', 'transactions:read')
 * @returns true if the user has the required permission
 */
export function hasPermission(userProfile: UserProfile | null, permission: string): boolean {
  // No profile or no role means no permissions
  if (!userProfile || !userProfile.role) {
    return false;
  }

  // Administrator role has full access
  if (userProfile.role === 'administrator') {
    return true;
  }

  // Check for wildcard permission (grants all permissions)
  if (userProfile.permissions.includes('*')) {
    return true;
  }

  // Check exact permission match
  if (userProfile.permissions.includes(permission)) {
    return true;
  }

  // Check category wildcard (e.g., 'security:*' covers 'security:manage')
  const [category] = permission.split(':');
  if (category && userProfile.permissions.includes(`${category}:*`)) {
    return true;
  }

  return false;
}

/**
 * Check if user has any of the specified permissions.
 *
 * @param userProfile - The user's profile from AuthContext
 * @param permissions - Array of permissions to check (OR logic)
 * @returns true if the user has at least one of the required permissions
 */
export function hasAnyPermission(userProfile: UserProfile | null, permissions: string[]): boolean {
  return permissions.some((permission) => hasPermission(userProfile, permission));
}

/**
 * Check if user has all of the specified permissions.
 *
 * @param userProfile - The user's profile from AuthContext
 * @param permissions - Array of permissions to check (AND logic)
 * @returns true if the user has all of the required permissions
 */
export function hasAllPermissions(userProfile: UserProfile | null, permissions: string[]): boolean {
  return permissions.every((permission) => hasPermission(userProfile, permission));
}

/**
 * Check if a user has any assigned role (is provisioned).
 *
 * @param userProfile - The user's profile from AuthContext
 * @returns true if the user has an assigned role
 */
export function isProvisioned(userProfile: UserProfile | null): boolean {
  return userProfile !== null && userProfile.role !== null;
}
